import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Session, Participant, Entry } from '../lib/types';

interface SessionState {
  session: Session | null;
  participant: Participant | null;
  entries: Entry[];
  participants: Participant[];
  setSession: (session: Session | null) => void;
  setParticipant: (participant: Participant | null) => void;
  setEntries: (entries: Entry[]) => void;
  addEntry: (entry: Entry) => void;
  setParticipants: (participants: Participant[]) => void;
  addParticipant: (participant: Participant) => void;
  updateParticipant: (participant: Participant) => void;
  removeParticipant: (participantId: string) => void;
  reset: () => void;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      session: null,
      participant: null,
      entries: [],
      participants: [],
      setSession: (session) => set({ session }),
      setParticipant: (participant) => set({ participant }),
      setEntries: (entries) => set({ entries }),
      addEntry: (entry) => {
        const currentEntries = get().entries;
        if (!currentEntries.some(e => e.id === entry.id)) {
          set({ entries: [...currentEntries, entry] });
        }
      },
      setParticipants: (participants) => {
        const sortedParticipants = [...participants].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        set({ participants: sortedParticipants });
      },
      addParticipant: (participant) => {
        const currentParticipants = get().participants;
        if (!currentParticipants.some(p => p.id === participant.id)) {
          const updatedParticipants = [...currentParticipants, participant].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          set({ participants: updatedParticipants });
        }
      },
      updateParticipant: (participant) => {
        const currentParticipants = get().participants;
        const updatedParticipants = currentParticipants.map(p => 
          p.id === participant.id ? participant : p
        );
        set({ participants: updatedParticipants });
      },
      removeParticipant: (participantId) => {
        set((state) => ({
          participants: state.participants.filter((p) => p.id !== participantId)
        }));
      },
      reset: () => set({ session: null, participant: null, entries: [], participants: [] }),
    }),
    {
      name: 'brainstorming-session',
      partialize: (state) => ({
        session: state.session,
        participant: state.participant
      })
    }
  )
);